import { Alert, Button, Card, Modal, Table } from "react-bootstrap";
import BuilderDocumentRow from "./BuilderDocumentRow";
import { useEffect, useState } from "react";
import { deleteBuyerDoc, getBuilderBuyerDocs, getBuilderDocs, uploadBuyerDoc } from "./BuilderDocFuncs";
import { lddGlobalSessionInfo, NavObjectType } from "../../navigationTypes";
import { useAppSelector } from "../../../redux/hooks";
import { lddStateObject } from "../../../redux/reducer";
import { getDataReader } from "../../../livedataFuncs";
import { BuilderDocType, BuilderStateType } from "./types";
import DeleteModal from "./Modals/DeleteModal";
import UploadModal from "./Modals/UploadModal";
import OverwriteModal from "./Modals/OverwriteModal";
import BuilderBanner from "../BuilderBanner";
import { launchLDDzoho } from "../../../dr/Components/drFuncs";
import { adMapper } from "../../../util/rpwConst";
import NewHomeInFile from "../InFile/NewHomeInFile";

export const BuilderDoc = () => {
    const livedataRootContainer = useAppSelector((state: lddStateObject) => state.currentNavigation.livedataRootContainer);
    const page: NavObjectType = useAppSelector((state) => state.currentNavigation.page);
    const sessionInfo: lddGlobalSessionInfo = useAppSelector((state) => state.SessionInfo);
    const [sellerDocs, setSellerDocs] =  useState<Array<BuilderDocType>>([]);
    const [buyerDocs, setBuyerDocs] = useState<Array<BuilderDocType>>([]);
    const [deleteModal, setDeleteModal] = useState<{
        show: boolean,
        dname?: string,
        docID?: number,
        msgID?: string
    }>({ show: false });
    const [showUploadModal, setShowUploadModal] = useState<boolean>(false);
    const [deleteLoading, setDeleteLoading] = useState<boolean>(false);
    const [uploadDocLoading, setUploadDocLoading] = useState<boolean>(false);
    const [sortingOrder, setSortingOrder] = useState<boolean>(false);
    const [showOverwriteModal, setShowOverwriteModal] = useState<boolean>(false);
    const [docToUpload, setDocToUpload] = useState<{name: string, ext: string}>({name: null, ext: null});
    const [builderState, setBuilderState] = useState<BuilderStateType>({appId: null, appCode: null, msgID: null});
    const [showWelcomeModal, setShowWelcomeModal] = useState<boolean>(false);

    useEffect(() => {
        const isFirstVisit = !sessionStorage.getItem('welcomeShown');
        
        if (!sessionInfo && isFirstVisit) {
            setShowWelcomeModal(true);
            sessionStorage.setItem('welcomeShown', 'true');
        }
    }, []);

    useEffect(() => {
        let id: string, appCode: string, msgID: string;
        if (livedataRootContainer?.dataRoot) {
            const DataReaderObj = getDataReader(livedataRootContainer.dataRoot);
            id = DataReaderObj.getAppId();
            appCode = DataReaderObj.getAppCode();
            msgID = DataReaderObj.getVIPInfoMsgID() || page.params?.MsgID;
            setBuilderState({appId: id, appCode: appCode, msgID: msgID});
        }
    }, [livedataRootContainer.dataRoot])

    useEffect(() => {
        if (builderState.msgID) {
            updateDocuments();
        }
    }, [builderState.msgID])
    
    const updateDocuments = async() => {
        updateSellerDocs();
        // updateBuyerDocs();
    }

    const updateSellerDocs = async() => {
        const DataReaderObj = getDataReader(livedataRootContainer.dataRoot);
        const docs = await getBuilderDocs(builderState.msgID, sessionInfo, page, DataReaderObj.getVIPInfoPassCode());
        for (let i = 0; i < docs.publicFiles?.length; i++) {
            docs.publicFiles[i].type = 'Project';
        }
        for (let i = 0; i < docs.privateFiles?.length; i++) {
            docs.privateFiles[i].type = 'Unit';
        }
        setSellerDocs([...docs?.privateFiles, ...docs?.publicFiles]);
    }

    const updateBuyerDocs = async() => {
        const buyerDocs = await getBuilderBuyerDocs(builderState.msgID, sessionInfo);
        setBuyerDocs(buyerDocs);
    }

    const handleDeleteBuyerDoc = async() => {
        setDeleteLoading(true);
        await deleteBuyerDoc(deleteModal.docID, deleteModal.msgID, sessionInfo);
        setDeleteLoading(false);
        setDeleteModal({ show: false, dname: null, docID: null });
        updateBuyerDocs();
    }

    const [uploadFile, setUploadFile] = useState<File>(null);


    const handleBuyerDocUpload = async(): Promise<void> => {
        try {
            setUploadDocLoading(true);
            // check existing file with duplicate name
            const isDuplicate = buyerDocs.findIndex((doc) => doc.filename === docToUpload.name);
            if (isDuplicate > -1) {
                setShowUploadModal(false);
                setShowOverwriteModal(true);
                setUploadDocLoading(false);
                return;
            }
            await uploadBuyerDoc(builderState.msgID, sessionInfo, uploadFile, docToUpload.name);
            setUploadDocLoading(false);
        } catch (error) {
            console.log(error);
            setUploadDocLoading(false);
        }
        setShowUploadModal(false);
        updateBuyerDocs();
        setUploadFile(null);
        setDocToUpload({name: null, ext: null});
    }

    const handleBuyerDocOverwrite = async(): Promise<void> => {
        try {
            setUploadDocLoading(true);
            await uploadBuyerDoc(builderState.msgID, sessionInfo, uploadFile, docToUpload.name);
            setUploadDocLoading(false);
        } catch (error) {
            console.log(error);
            setUploadDocLoading(false);
        }
        setShowOverwriteModal(false);
        updateBuyerDocs();
        setUploadFile(null);
        setDocToUpload({name: null, ext: null});
    }

    const handleCancelOverwriteModal = () => {
        setDocToUpload({name: null, ext: null});
        setUploadFile(null);
        setShowOverwriteModal(false);
    }

    const handleClick = (sender: 'Seller'|'Buyer', column: 'filename' | 'timestamp' | 'type' | 'read') => {
        const sortDocuments = (docs) => {
            return docs.sort((a, b) => {
                const aValue = a[column]?.toString().toLowerCase() ?? "1";
                const bValue = b[column]?.toString().toLowerCase() ?? "1";
                if ((bValue && !aValue) || (aValue && bValue && (aValue < bValue))) {
                    return sortingOrder ? 1 : -1;
                } else {
                    return sortingOrder ? -1 : 1;
                }
            });
        };
        if (sender === 'Seller') {
            setSellerDocs(sortDocuments(sellerDocs));
        } else {
            setBuyerDocs(sortDocuments(buyerDocs));
        }
        setSortingOrder(!sortingOrder);
    }

    function tableHeader(sender: 'Seller'|'Buyer'): JSX.Element {
        return (
            <thead>
                <tr>
                    <th style={{ width: '30px' }}></th>
                    <th onClick={() => {handleClick(sender, 'filename')}}><span style={{cursor: 'pointer'}}><i className="fa-solid fa-sort fa-indicator"></i>Name</span></th>
                    <th onClick={() => handleClick(sender, 'read')} style={{ width: '40px' }}><span style={{cursor: 'pointer'}}><i className="fa-solid fa-sort fa-indicator"></i>Status</span></th>
                    <th onClick={() => handleClick(sender, 'type')} style={{ width: '40px' }}><span style={{cursor: 'pointer'}}><i className="fa-solid fa-sort fa-indicator"></i>Type</span></th>
                    <th onClick={() => {handleClick(sender, 'timestamp')}} style={{ width: '190px' }}><span style={{cursor: 'pointer'}}><i className="fa-solid fa-sort fa-indicator"></i>Uploaded</span></th>
                    <th></th>
                </tr>
            </thead>
        );
    }

    function sellerTableBody(): JSX.Element {
        let docsData = sellerDocs;
        return (<tbody>
                {docsData.map((doc: BuilderDocType, index: number) => {
                    return <BuilderDocumentRow
                        FileName={doc.filename}
                        FileType={doc.type}
                        Link={doc.href}
                        Timestamp={doc.timestamp}
                        SessionInfo={sessionInfo}
                        setDeleteModal={(setDeleteModal)}
                        key={index}
                        Note={doc.note}
                        Read={doc.read}
                    />
                })}
                {(!docsData || docsData.length === 0) &&
                    <tr><td colSpan={6}><Alert variant="primary">No documents to display.</Alert></td></tr>
                }
            </tbody>);
    }

    const DataReaderObj = getDataReader(livedataRootContainer.dataRoot);
    if (!DataReaderObj.getVIPInfoMsgID() && page.params?.PassCode !== "1") {
        return <NewHomeInFile/>
    }

    return <>
            <Modal 
                show={showWelcomeModal} 
                onHide={() => setShowWelcomeModal(false)}
                centered
                size="lg"
                backdrop="static"
                keyboard={false}
                contentClassName="p-0"  // Add this line
            >
                        <img 
                            src="/dr/Splash2.png" 
                            alt="Welcome to DR" 
                            useMap="#welcomeMap"
                            style={{ width: '100%', height: 'auto', display: 'block' }}
                        />
                        <map name="welcomeMap">
                        <area 
                            shape="rect" 
                            coords="50,340,285,375"
                            href="#"
                            onClick={(e) => {
                                e.preventDefault();
                                launchLDDzoho("https://zfrmz.com/cdIabRvui3fve5FCSqvc", sessionInfo, adMapper.Splash2);
                            }}
                            rel="noopener noreferrer"
                            alt="Talk to an Expert"
                        />
                        <area 
                            shape="rect" 
                            coords="50,392,220,410"
                            href="#" 
                            onClick={(e) => {
                                e.preventDefault();
                                setShowWelcomeModal(false);
                            }}
                            alt="Continue to File"
                        />
                    </map>
            </Modal>
        <DeleteModal 
            show={deleteModal.show} 
            docname={deleteModal.dname} 
            isLoading={deleteLoading} 
            setDeleteModal={setDeleteModal} 
            handleDelete={handleDeleteBuyerDoc}
        />
        <UploadModal 
            showModal={showUploadModal} 
            handleBuyerDocUpload={handleBuyerDocUpload} 
            uploadDocLoading={uploadDocLoading} 
            file={uploadFile} 
            setFile={setUploadFile}
            setShowModal={setShowUploadModal}
            docUploadName={docToUpload.name}
            setDocToUpload={setDocToUpload}
        />
        <OverwriteModal 
            show={showOverwriteModal} 
            docname={docToUpload.name + '.' + docToUpload.ext} 
            isLoading={uploadDocLoading} 
            handleOverwrite={handleBuyerDocOverwrite} 
            handleCancel={handleCancelOverwriteModal}
        />
        <div>
            <BuilderBanner sessionInfo={sessionInfo} msgID={builderState.msgID} showImport={false}/>
        </div>
        <div style={{ height: 'calc(100% - 50px)'}}>
            <Card className="card-list dr-card" style={{ margin: '0', maxHeight:'100%'}}>
                <Card.Header>
                    <div>
                        <Button variant="link-text" title="Refresh" onClick={updateDocuments}><i className="fa-regular fa-rotate"></i></Button>
                        <Button variant="link-text" title="home"><i className="fa-regular fa-house"></i></Button>
                        <b>/ NewHome Documents</b>
                    </div>
                </Card.Header>

                <Card.Body>
                    <Table hover className="sortable">
                        {tableHeader('Seller')}
                        {sellerTableBody()}
                    </Table>
                </Card.Body>
            </Card>
            
            {/* <div style={{height: '40%'}}>
                <Card className="card-list" style={{ height: '100%', width: '100%'}}>
                    <Card.Header>
                        <b>/ Share with Builder</b>
                        <div className="card-tools">
                            <Button variant="tool" title="Upload new document" onClick={(e) => {setShowUploadModal(true)}}><i className="fa-light fa-arrow-up-to-line"></i> Upload</Button>
                        </div>
                    </Card.Header>

                    <Card.Body style={{ overflow: 'auto' }}>
                        <Table hover>
                            {tableHeader('Buyer')}
                            {buyerTableBody()}
                        </Table>
                    </Card.Body>
                </Card>
            </div>  */}
        </div>
    </>
}